import {
  sanitizeFilterGroup,
  type FilterGroup,
  type FilterOperatorData
} from '@Core/components/dynamicFields/filter/types';
import { isRecord } from '@Core/utils/recordGuards';

function isPersistedAdvancedFilter(value: unknown): value is FilterGroup {
  if (!isRecord(value) || !Array.isArray(value.conditions)) {
    return false;
  }

  return value.operator === 'and' || value.operator === 'or' || value.operator === 'nor';
}

export function normalizePersistedResourceDataTableAdvancedFilter(
  value: unknown
): FilterGroup | null {
  if (!isPersistedAdvancedFilter(value)) {
    return null;
  }

  return sanitizeFilterGroup(structuredClone(value));
}

/**
 * Full operator set offered to the resource data-table filter builder, in the
 * display order used by the visual builder. Mirrors the backend
 * `FilterInjectionData::allOperators()` (`FilterOperator::displayOrder()`). The
 * resource data table is a purely client-side filter, so it carries its own copy. Per-field-type
 * narrowing (and hiding of operators like `elem_match`) happens in `getCompatibleOperators`.
 */
export const RESOURCE_DATA_TABLE_FILTER_OPERATORS: Record<string, FilterOperatorData> = {
  '=': { value: '=', label: 'Equals', value_shape: 'single' },
  '!=': { value: '!=', label: 'Not equals', value_shape: 'single' },
  is: { value: 'is', label: 'Is', value_shape: 'single' },
  is_not: { value: 'is_not', label: 'Is not', value_shape: 'single' },
  '>': { value: '>', label: 'Greater than', value_shape: 'single' },
  '>=': { value: '>=', label: 'Greater than or equal', value_shape: 'single' },
  '<': { value: '<', label: 'Less than', value_shape: 'single' },
  '<=': { value: '<=', label: 'Less than or equal', value_shape: 'single' },
  between: { value: 'between', label: 'Between', value_shape: 'range' },
  contains: { value: 'contains', label: 'Contains', value_shape: 'single' },
  starts_with: { value: 'starts_with', label: 'Starts with', value_shape: 'single' },
  ends_with: { value: 'ends_with', label: 'Ends with', value_shape: 'single' },
  regex: { value: 'regex', label: 'Matches regex', value_shape: 'regex' },
  in: { value: 'in', label: 'In', value_shape: 'array' },
  not_in: { value: 'not_in', label: 'Not in', value_shape: 'array' },
  exists: { value: 'exists', label: 'Exists', value_shape: 'none' },
  not_exists: { value: 'not_exists', label: 'Does not exist', value_shape: 'none' },
  is_null: { value: 'is_null', label: 'Is null', value_shape: 'none' },
  is_not_null: { value: 'is_not_null', label: 'Is not null', value_shape: 'none' },
  has_value: { value: 'has_value', label: 'Has value', value_shape: 'none' },
  missing_value: { value: 'missing_value', label: 'Missing value', value_shape: 'none' },
  all: { value: 'all', label: 'Contains all', value_shape: 'array' },
  elem_match: { value: 'elem_match', label: 'Element matches', value_shape: 'nested' },
  mod: { value: 'mod', label: 'Modulo', value_shape: 'divisor_remainder' },
  not: { value: 'not', label: 'Not', value_shape: 'single' }
};

function isAuthoredFilterNodeList(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.every((entry) => isRecord(entry));
}

export function stableResourceDataTableFilterKey(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

export function normalizeAuthoredResourceDataTableFilter(value: unknown): FilterGroup | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (isAuthoredFilterNodeList(value)) {
    return sanitizeFilterGroup({
      operator: 'and',
      conditions: structuredClone(value)
    } as FilterGroup);
  }

  if (isPersistedAdvancedFilter(value)) {
    return sanitizeFilterGroup(structuredClone(value));
  }

  return null;
}

export function composeResourceDataTableFilters(
  authoredFilters: FilterGroup | null,
  runtimeFilters: FilterGroup | null
): FilterGroup | null {
  if (!authoredFilters) {
    return runtimeFilters;
  }

  if (!runtimeFilters) {
    return authoredFilters;
  }

  return {
    operator: 'and',
    conditions: [authoredFilters, runtimeFilters]
  };
}
